import { useEffect, useState, type FormEvent } from "react";
import { useOutletContext } from "react-router";
import { api, ApiError } from "@/lib/api";
import type { AdminContext } from "@/components/admin/AdminShell";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

type StaffMember = {
  id: string;
  email: string;
  name: string | null;
  role: "admin" | "staff";
  createdAt: string;
};

export default function AdminStaff() {
  const { role, email: myEmail } = useOutletContext<AdminContext>();
  const [list, setList] = useState<StaffMember[]>([]);
  const [loaded, setLoaded] = useState(false);

  // create form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [newRole, setNewRole] = useState<"staff" | "admin">("staff");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    const r = await api.get<{ staff: StaffMember[] }>("/api/admin/staff");
    setList(r.staff);
  }

  useEffect(() => {
    if (role !== "admin") return;
    load().finally(() => setLoaded(true));
  }, [role]);

  if (role !== "admin") {
    return (
      <section>
        <style>{css}</style>
        <h1 className="aflt-admin-title">Staff</h1>
        <p className="intro">Only admins can manage staff accounts.</p>
      </section>
    );
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    setNotice(null);
    try {
      await api.post("/api/admin/staff", { email, name, password, role: newRole });
      setNotice(`Created ${email}. Share the password with them to sign in.`);
      setEmail("");
      setName("");
      setPassword("");
      setNewRole("staff");
      await load();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not create account");
    } finally {
      setSaving(false);
    }
  }

  async function changeRole(m: StaffMember, to: "staff" | "admin") {
    setErr(null);
    setNotice(null);
    try {
      await api.patch(`/api/admin/staff/${m.id}`, { role: to });
      await load();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not change role");
    }
  }

  async function remove(m: StaffMember) {
    if (!confirm(`Remove ${m.email}? This deletes their account and sign-in access.`)) return;
    setErr(null);
    setNotice(null);
    try {
      await api.del(`/api/admin/staff/${m.id}`);
      await load();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not remove account");
    }
  }

  return (
    <section>
      <style>{css}</style>
      <h1 className="aflt-admin-title">Staff</h1>
      <p className="intro">
        Staff can access the whole admin panel except the integration credentials
        (Stripe, Klaviyo, Discord keys) and this page. Admins can do everything
        and manage staff.
      </p>

      {/* ── Add staff ──────────────────────────────────── */}
      <form onSubmit={create} className="paper-card">
        <legend className="form-legend">Add an account</legend>
        {notice && <div className="test-banner ok">✓ {notice}</div>}
        <div className="grid2">
          <label className="fld">
            <span>Name</span>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
          </label>
          <label className="fld">
            <span>Email</span>
            <input
              className="inp"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
            />
          </label>
          <label className="fld">
            <span>Temporary password</span>
            <input
              className="inp"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="at least 8 characters"
            />
          </label>
          <label className="fld">
            <span>Role</span>
            <select className="inp" value={newRole} onChange={(e) => setNewRole(e.target.value as "staff" | "admin")}>
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </label>
        </div>
        {err && <p className="err">{err}</p>}
        <div className="actions">
          <button type="submit" className="btn-woven" disabled={saving}>
            {saving ? "Creating…" : "Create account"}
          </button>
        </div>
      </form>

      {/* ── Existing ───────────────────────────────────── */}
      <div className="paper-card" style={{ marginTop: "1.5rem" }}>
        <legend className="form-legend">Accounts</legend>
        {!loaded ? (
          <p className="muted">Loading…</p>
        ) : list.length === 0 ? (
          <p className="muted">No staff or admin accounts yet.</p>
        ) : (
          <table className="staff-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                const isMe = m.email === myEmail;
                return (
                  <tr key={m.id}>
                    <td>{m.name || "—"}</td>
                    <td>
                      {m.email}
                      {isMe && <span className="you">you</span>}
                    </td>
                    <td>
                      <span className={`role-chip ${m.role}`}>{m.role}</span>
                    </td>
                    <td className="row-actions">
                      {m.role === "staff" ? (
                        <button className="btn-mini" onClick={() => changeRole(m, "admin")}>
                          Make admin
                        </button>
                      ) : (
                        <button className="btn-mini" disabled={isMe} onClick={() => changeRole(m, "staff")}>
                          Make staff
                        </button>
                      )}
                      <button className="btn-mini danger" disabled={isMe} onClick={() => remove(m)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

const css = `
  .intro {
    font-family: ${fonts.body}; font-size: 0.875rem; color: rgba(8,45,61,0.65);
    max-width: 48rem; line-height: 1.55; margin-bottom: 1.5rem;
  }
  .muted { font-family: ${fonts.body}; color: rgba(8,45,61,0.55); font-size: 0.9rem; }
  .paper-card {
    background: #fff; border: 1.5px dashed rgba(201,147,76,0.40);
    border-radius: 16px; padding: 1.5rem 1.75rem;
  }
  .form-legend {
    font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem;
    color: ${colors.navy}; display: block; margin-bottom: 1.25rem;
  }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  @media (max-width: 640px) { .grid2 { grid-template-columns: 1fr; } }
  .fld { display: block; margin-bottom: 0.25rem; }
  .fld > span {
    display: flex; align-items: center; gap: 0.4rem;
    font-family: ${fonts.body}; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65);
    margin-bottom: 0.35rem;
  }
  .inp {
    width: 100%; padding: 0.55rem 0.9rem;
    border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy};
    font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
    box-sizing: border-box;
  }
  .inp:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  select.inp { appearance: none; cursor: pointer; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.75rem; flex-wrap: wrap; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px;
    box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .err {
    color: ${colors.berryRose}; font-family: ${fonts.body};
    font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0 0;
  }
  .test-banner { padding: 0.6rem 0.9rem; border-radius: 0.5rem; margin-bottom: 0.75rem;
    font-weight: 600; font-family: ${fonts.body}; font-size: 0.875rem; }
  .test-banner.ok { background: #606b4c; color: #fff8ed; }
  .staff-table { width: 100%; border-collapse: collapse; font-family: ${fonts.body}; }
  .staff-table th {
    text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.12em; color: rgba(8,45,61,0.5); padding: 0 0.5rem 0.6rem; border-bottom: 1px dashed rgba(201,147,76,0.3);
  }
  .staff-table td {
    padding: 0.7rem 0.5rem; font-size: 13.5px; color: ${colors.navy};
    border-bottom: 1px dashed rgba(201,147,76,0.18); vertical-align: middle;
  }
  .you {
    margin-left: 0.4rem; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.1em; color: rgba(8,45,61,0.45);
  }
  .role-chip {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em;
    padding: 0.15rem 0.5rem; border-radius: 999px;
  }
  .role-chip.admin { background: ${colors.berryRose}; color: #fff; }
  .role-chip.staff { background: ${colors.sageLight}; color: ${colors.forestGreen}; }
  .row-actions { display: flex; gap: 0.4rem; justify-content: flex-end; }
  .btn-mini {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.65rem; font-weight: 700; letter-spacing: 0.04em;
    text-transform: uppercase; border-radius: 6px; padding: 6px 10px; cursor: pointer; white-space: nowrap;
  }
  .btn-mini:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-mini:disabled { opacity: 0.4; cursor: default; }
  .btn-mini.danger { color: ${colors.berryRose}; border-color: rgba(212,96,106,0.5); }
`;
